import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
  DateInput
} from '../../common/Input';
import { ConfirmationModal } from '../../common/Modal';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import { Select } from "../../common/Select";

function Holiday({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Description",
    "Holiday Date",
    "Holiday Type",
    "Location",
    "Actions"
  ]
  
  var accessors = [
    "id",
    "description",
    "date",
    "holidayTypeDescription",
    "locationDisplay",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Dropdown options
  const [holidayTypeOptions, setHolidayTypeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    description: "",
    date: "",
    holidayTypeId: "",
    isAllLocation: false,
    locations: [] // Array of location objects {id, description}
  });

  const resetLocalVariables = () => {
    setForm({
      description: "",
      date: "",
      holidayTypeId: "",
      isAllLocation: false,
      locations: []
    });
    setSelectedData(null);
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);
  // Delete
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const apiUrl = domain + "/api/Holiday";
  const holidayTypesUrl = domain + "/api/HolidayType/active";
  const locationsUrl = domain + "/api/Location/active";

  // Fetch dropdown data from API
  const fetchDropdowns = useCallback(async () => {
    try {
      setDropdownsLoading(true);
      
      // Fetch holiday types
      const holidayTypesResponse = await axios.get(holidayTypesUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const holidayTypeOptions = holidayTypesResponse.data.map(item => ({
        value: item.id,
        label: item.description
      }));

      setHolidayTypeOptions(holidayTypeOptions);

      // Fetch locations
      const locationsResponse = await axios.get(locationsUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Sort locations by description
      const sortedLocations = locationsResponse.data.sort((a, b) => 
        a.description.localeCompare(b.description)
      );

      const locationOptions = sortedLocations.map(item => ({
        value: item.id,
        label: item.description
      }));

      setLocationOptions(locationOptions);

    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    } finally {
      setDropdownsLoading(false);
    }
  }, [holidayTypesUrl, locationsUrl]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Transform data for display
      const transformedData = response.data.map(item => ({
        ...item,
        locationDisplay: item.isAllLocation 
          ? "All Locations" 
          : item.locations?.map(loc => loc.locationDescription).join(", ") || "No locations",
        dateDisplay: item.date ? formatDateForDisplay(item.date) : ""
      }));
      
      setData(transformedData);
      setFilterData(transformedData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDropdowns();
    fetchData();
  }, [fetchDropdowns, fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    
    if (!value) {
      // If input is empty, reset to original list
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      const match3 = f.holidayTypeDescription?.toString().toLowerCase().includes(lowercaseValue);
      const match4 = f.locationDisplay?.toString().toLowerCase().includes(lowercaseValue);
      return match1 || match2 || match3 || match4;
    });
    
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prepare data for API
    const apiData = {
      description: form.description || "",
      date: form.date ? formatDateForAPI(form.date) : "",
      holidayTypeId: form.holidayTypeId ? parseInt(form.holidayTypeId) : null,
      isAllLocation: form.isAllLocation,
      locations: form.locations.map(loc => ({
        locationId: parseInt(loc.value)
      }))
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Holiday", 
        actionTaken: "insert", 
        field: "Add", 
        newValue: `${apiData.description} (${formatDateForDisplay(apiData.date)})`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("Holiday created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create holiday";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    const apiData = {
      description: form.description || "",
      date: form.date ? formatDateForAPI(form.date) : "",
      holidayTypeId: form.holidayTypeId ? parseInt(form.holidayTypeId) : null,
      isAllLocation: form.isAllLocation,
      locations: form.locations.map(loc => ({
        locationId: parseInt(loc.value)
      }))
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      let auditInfo = [];

      if(apiData.description !== selectedData.description){
        auditInfo.push({
          setupArea: "Holiday",
          actionTaken: "update",
          field: "Description",
          newValue: `${apiData.description}`,
          oldValue: `${selectedData.description}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.date !== selectedData.date){
        auditInfo.push({
          setupArea: "Holiday",
          actionTaken: "update",
          field: "Date",
          newValue: `${formatDateForDisplay(apiData.date)}`,
          oldValue: `${formatDateForDisplay(selectedData.date)}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.holidayTypeId !== selectedData.holidayTypeId){
        const newHolidayType = holidayTypeOptions.find(opt => opt.value === parseInt(apiData.holidayTypeId));
        const oldHolidayType = holidayTypeOptions.find(opt => opt.value === parseInt(selectedData.holidayTypeId));
        
        auditInfo.push({
          setupArea: "Holiday",
          actionTaken: "update",
          field: "Holiday Type",
          newValue: newHolidayType ? newHolidayType.label : apiData.holidayTypeId,
          oldValue: oldHolidayType ? oldHolidayType.label : selectedData.holidayTypeId,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.isAllLocation !== selectedData.isAllLocation){
        auditInfo.push({
          setupArea: "Holiday",
          actionTaken: "update",
          field: "Is All Location?",
          newValue: `${apiData.isAllLocation ? "Yes" : "No"}`,
          oldValue: `${selectedData.isAllLocation ? "Yes" : "No"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      // Compare locations
      const newLocationIds = apiData.locations.map(loc => loc.locationId).sort();
      const oldLocationIds = selectedData.locations?.map(loc => loc.locationId).sort() || [];
      
      if (JSON.stringify(newLocationIds) !== JSON.stringify(oldLocationIds)) {
        const newLocationNames = form.locations.map(loc => loc.label).join(", ");
        const oldLocationNames = selectedData.locations?.map(loc => loc.locationDescription).join(", ") || "None";
        
        auditInfo.push({
          setupArea: "Holiday",
          actionTaken: "update",
          field: "Locations",
          newValue: apiData.isAllLocation ? "All Locations" : newLocationNames,
          oldValue: selectedData.isAllLocation ? "All Locations" : oldLocationNames,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({data: auditInfo});
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Holiday updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update holiday";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedData?.id) {
      showGeneralMessage("No record selected for deletion", false);
      return;
    }

    try {
      setLoadingRequest(true);
      await axios.delete(`${apiUrl}/${selectedData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Holiday", 
        actionTaken: "delete", 
        field: "Delete", 
        newValue: `${selectedData.description} (${formatDateForDisplay(selectedData.date)})`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("Holiday deleted successfully!", true);
      closeDeleteHandleClick();
    } catch (error) {
      console.error('Error deleting data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to delete holiday";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    setForm({
      description: data.description || "",
      date: data.date ? formatDateForForm(data.date) : "",
      holidayTypeId: data.holidayTypeId?.toString() || "",
      isAllLocation: data.isAllLocation || false,
      locations: data.locations?.map(loc => ({
        value: loc.locationId,
        label: loc.locationDescription
      })) || []
    });
    openEditHandleClick();
  };

  const deleteHandleClick = (data) => {
    setSelectedData(data);
    setShowDeleteForm(true);
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const openDeleteHandleClick = () => {
    setShowDeleteForm(true);
  };

  const closeDeleteHandleClick = () => {
    setShowDeleteForm(false);
    setSelectedData(null);
  };

  const addLocation = (locationId) => {
    if (!locationId) return;
    
    const selectedLocation = locationOptions.find(opt => opt.value === parseInt(locationId));
    if (!selectedLocation) return;

    // Check if already added
    if (form.locations.some(loc => loc.value === selectedLocation.value)) {
      showGeneralMessage(`${selectedLocation.label} is already added.`, false);
      return;
    }

    setForm(prev => ({
      ...prev,
      locations: [...prev.locations, selectedLocation]
    }));
  };

  const removeLocation = (locationValue) => {
    setForm(prev => ({
      ...prev,
      locations: prev.locations.filter(loc => loc.value !== locationValue)
    }));
  };

  const handleIsAllLocationChange = (checked) => {
    setForm(prev => ({
      ...prev,
      isAllLocation: checked,
      locations: checked ? [] : prev.locations
    }));
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
    {
      name: "Delete",
      tooltip: "delete",
      icon: <DeleteIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600 flex items-center",
      function: deleteHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Holiday"}</span>
              </div>
              <h3>Holiday</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Holiday"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"Holidays"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Form */}
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Holiday"}
        mode="add"
        holidayTypeOptions={holidayTypeOptions}
        locationOptions={locationOptions}
        dropdownsLoading={dropdownsLoading}
        addLocation={addLocation}
        removeLocation={removeLocation}
        handleIsAllLocationChange={handleIsAllLocationChange}
      />
      
      {/* Edit Form */}
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Holiday"}
        mode="edit"
        holidayTypeOptions={holidayTypeOptions}
        locationOptions={locationOptions}
        dropdownsLoading={dropdownsLoading}
        addLocation={addLocation}
        removeLocation={removeLocation}
        handleIsAllLocationChange={handleIsAllLocationChange}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteForm}
        onClose={closeDeleteHandleClick}
        onConfirm={handleDelete}
        title="Delete Holiday"
        message="Are you sure you want to delete this holiday?"
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={loadingRequest}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading || dropdownsLoading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add", // "add", "edit"
  holidayTypeOptions,
  locationOptions,
  dropdownsLoading,
  addLocation,
  removeLocation,
  handleIsAllLocationChange
}) {
  // Add the useGeneralMessage hook here
  const { showGeneralMessage } = useGeneralMessage();
  
  const [errorState, setErrorState] = useState({
    description: false,
    date: false,
    holidayTypeId: false
  });
  const [selectedLocationId, setSelectedLocationId] = useState("");

  const submit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrorState = {
      description: !form.description,
      date: !form.date,
      holidayTypeId: !form.holidayTypeId
    };
    
    if (newErrorState.description || newErrorState.date || newErrorState.holidayTypeId) {
      setErrorState(newErrorState);
      return;
    }

    // Validate locations if not all locations
    if (!form.isAllLocation && form.locations.length === 0) {
      showGeneralMessage("Please add at least one location or select 'All Locations'", false);
      return;
    }
    
    handleSubmit(e);
    setErrorState({ description: false, date: false, holidayTypeId: false });
  };

  const handleClose = () => {
    onClose();
    setErrorState({ description: false, date: false, holidayTypeId: false });
    setSelectedLocationId("");
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddLocation = () => {
    if (!selectedLocationId) {
      showGeneralMessage("Please select a location first", false);
      return;
    }
    addLocation(selectedLocationId);
    setSelectedLocationId("");
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[50%] xl:w-[50%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-grow space-y-4 overflow-y-auto p-6">

            {/* Holiday Form */}
            <div className="rounded-lg border border-gray-300 bg-white px-4 shadow-sm">
              <div className="border-b border-gray-300 py-1">
                <h3 className="font-medium text-gray-700">Holiday Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Description:"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter holiday description"
                  isRequired={true}
                  error={errorState.description && !form.description}
                  errorMessage="Please fill up this field"
                />
                
                <DateInput
                  label="Date:"
                  value={form.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  placeholder="Select holiday date"
                  isRequired={true}
                  error={errorState.date && !form.date}
                  errorMessage="Please fill up this field"
                />
                
                <Select
                  label="Holiday Type:"
                  value={form.holidayTypeId}
                  onChange={(e) => handleInputChange('holidayTypeId', e.target.value)}
                  options={holidayTypeOptions}
                  placeholder={dropdownsLoading ? "Loading holiday types..." : "Select holiday type"}
                  isRequired={true}
                  isDisabled={dropdownsLoading}
                  error={errorState.holidayTypeId && !form.holidayTypeId}
                  errorMessage="Please fill up this field"
                />
                
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isAllLocation}
                    onChange={(e) => handleIsAllLocationChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    All Locations
                  </label>
                </div>

                {!form.isAllLocation && (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center space-x-2">
                      <Select
                        label="Add Location:"
                        value={selectedLocationId}
                        onChange={(e) => setSelectedLocationId(e.target.value)}
                        options={locationOptions}
                        placeholder="Select location"
                        className="flex-grow"
                        isDisabled={dropdownsLoading}
                      />
                      <button
                        type="button"
                        onClick={handleAddLocation}
                        className="mt-6 rounded border-none bg-green-500 px-2.5 py-2 text-sm text-white hover:bg-green-600"
                        disabled={dropdownsLoading}
                      >
                        Add
                      </button>
                    </div>
                    
                    {form.locations.length > 0 && (
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-600">
                          Selected Locations ({form.locations.length}):
                        </label>
                        <div className="max-h-40 overflow-y-auto rounded border border-gray-200 p-2">
                          {form.locations.map((location) => (
                            <div key={location.value} className="flex items-center justify-between px-2 py-1 hover:bg-gray-50">
                              <span>{location.label}</span>
                              <button
                                type="button"
                                onClick={() => removeLocation(location.value)}
                                className="rounded-md border-none bg-red-400 px-2 py-1 text-sm text-white hover:bg-red-500"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              disabled={dropdownsLoading}
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

// Helper functions
function formatDateForDisplay(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  
  return `${month}/${day}/${year}`;
}

function formatDateForForm(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  
  return date.toISOString().split('T')[0];
}

function formatDateForAPI(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  
  return date.toISOString();
}

export default Holiday;